/**
 * X OAuth 1.0a Modal - SIMPLIFIED
 * ================================
 * P0 REFACTOR: Single authentication method for X integration.
 * 
 * This modal handles:
 * - API Key (Consumer Key) + API Secret (Consumer Secret) input
 * - OAuth 1.0a authorization flow
 * - Connection status display
 * - Disconnect functionality
 * 
 * Platform ID is ALWAYS "x" (never "x_twitter")
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Copy,
  Check,
  ExternalLink,
  AlertCircle,
  Loader2,
  Key,
  Link2,
  CheckCircle2,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';

// Production domain for callback URL
const PRODUCTION_DOMAIN = 'https://ai-marketing-engine.com';
const CALLBACK_PATH = '/api/integrations/x/oauth1/callback';

// Platform ID - ALWAYS "x"
const PLATFORM_ID = 'x';

/**
 * X OAuth 1.0a Configuration Modal
 */
export function XOAuth1Modal({ isOpen, onClose, projectId, onStatusChange }) {
  // State
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Form state
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  
  // Callback URL
  const callbackUrl = `${PRODUCTION_DOMAIN}${CALLBACK_PATH}`;
  
  // Fetch status on mount and when modal opens
  const fetchStatus = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      // P0 UNIFIED: Use standard endpoint (no -new suffix)
      const response = await apiClient.get(`/integrations/x/status?project_id=${projectId}`);
      setStatus(response.data);
      onStatusChange?.(response.data);
    } catch (error) {
      console.error('[XOAuth1Modal] Failed to fetch status:', error);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, onStatusChange]);
  
  useEffect(() => {
    if (isOpen && projectId) {
      fetchStatus();
    }
  }, [isOpen, projectId, fetchStatus]);
  
  // Handle URL callback parameters (from OAuth redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthResult = params.get('x_oauth1');
    const screenName = params.get('screen_name');
    const errorCode = params.get('code');
    const timestamp = params.get('ts');
    
    if (oauthResult === 'success') {
      toast.success(`Connecté à X en tant que @${screenName || 'utilisateur'}`);
      
      // P0 FIX: Clean URL immediately
      params.delete('x_oauth1');
      params.delete('screen_name');
      params.delete('project_id');
      params.delete('ts');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
      
      // P0 FIX: Force immediate status refresh with small delay to ensure backend has committed
      setTimeout(() => {
        fetchStatus();
        // Dispatch global event for other components
        window.dispatchEvent(new CustomEvent('ame:x-connected', {
          detail: { screenName, projectId, timestamp: Date.now() }
        }));
      }, 500);
      
    } else if (oauthResult === 'error') {
      const errorMessages = {
        'INVALID_VERIFIER': 'Session expirée. Veuillez réessayer.',
        'ACCESS_TOKEN_FAILED': 'Échec de l\'autorisation X.',
        'MISSING_CREDENTIALS': 'Credentials manquants.',
        'CALLBACK_NOT_APPROVED': 'URL de callback non approuvée dans X Developer Portal.'
      };
      toast.error(errorMessages[errorCode] || `Erreur X: ${errorCode}`);
      
      // Clean URL
      params.delete('x_oauth1');
      params.delete('code');
      params.delete('ts');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
    }
  }, [fetchStatus, projectId]);
  
  // Copy callback URL
  const handleCopyCallback = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
      setCopied(true);
      toast.success('URL copiée !');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Impossible de copier');
    }
  };
  
  // Save credentials
  const handleSaveCredentials = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setSaving(true);
    try {
      // P0 UNIFIED: Use standard endpoint
      await apiClient.post('/integrations/x/credentials', {
        project_id: projectId,
        api_key: apiKey.trim(),
        api_secret: apiSecret.trim()
      });
      
      toast.success('Credentials sauvegardés');
      setApiKey('');
      setApiSecret('');
      await fetchStatus();
    } catch (error) {
      console.error('[XOAuth1Modal] Save credentials error:', error);
      toast.error(error.response?.data?.detail?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  // Start OAuth flow
  const handleConnect = async () => {
    setConnecting(true);
    try {
      // P0 UNIFIED: Use standard endpoint
      const response = await apiClient.post('/integrations/x/oauth1/start', {
        project_id: projectId
      });
      
      const { authorization_url, callback_url_expected } = response.data;
      
      if (authorization_url) {
        // Redirect to X for authorization
        window.location.href = authorization_url;
      } else {
        toast.error('URL d\'autorisation non reçue');
      }
    } catch (error) {
      console.error('[XOAuth1Modal] Connect error:', error);
      const detail = error.response?.data?.detail;
      
      if (detail?.error_code === 'CALLBACK_NOT_APPROVED') {
        toast.error(
          <div>
            <p>URL de callback non approuvée dans le portail X.</p>
            <p className="text-xs mt-1 opacity-70">
              Ajoutez cette URL: {detail.callback_url_expected}
            </p>
          </div>,
          { duration: 8000 }
        );
      } else if (detail?.error_code === 'MISSING_CREDENTIALS') {
        toast.error('Veuillez d\'abord configurer vos API Key et Secret');
      } else {
        toast.error(detail?.message || 'Erreur lors de la connexion');
      }
    } finally {
      setConnecting(false);
    }
  };
  
  // Disconnect
  const handleDisconnect = async () => {
    if (!confirm('Déconnecter votre compte X ?')) return;
    
    setLoading(true);
    try {
      // P0 UNIFIED: Use standard endpoint
      await apiClient.delete(`/integrations/x/disconnect?project_id=${projectId}`);
      toast.success('Compte X déconnecté');
      await fetchStatus();
    } catch (error) {
      console.error('[XOAuth1Modal] Disconnect error:', error);
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setLoading(false);
    }
  };
  
  // Hard reset - removes ALL X data + purges client state
  const handleHardReset = async () => {
    if (!confirm('Réinitialiser complètement l\'intégration X ?\n\nCela supprimera:\n- API Key/Secret\n- Tokens d\'accès\n- Toute la configuration\n\nVous devrez tout reconfigurer.')) return;
    
    setLoading(true);
    try {
      // P0 FIX: Call reset endpoint
      await apiClient.post('/integrations/x/reset', {
        project_id: projectId
      });
      
      // P0 FIX: Purge ALL client-side state
      // 1. Clear form fields
      setApiKey('');
      setApiSecret('');
      
      // 2. Reset local status to clean state
      setStatus({
        ok: true,
        platform: 'x',
        auth_type: 'oauth1',
        connected: false,
        has_tokens: false,
        has_oauth1_tokens: false,
        has_credentials: false,
        screen_name: null,
        oauth1: { connected: false, has_tokens: false, screen_name: null, enabled: false }
      });
      
      // 3. Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('ame:x-reset', {
        detail: { projectId, timestamp: Date.now() }
      }));
      
      toast.success('Intégration X réinitialisée');
      
      // 4. Force re-fetch from server to ensure sync
      await fetchStatus();
      
      // 5. Notify parent component
      onStatusChange?.({ connected: false, has_tokens: false });
      
    } catch (error) {
      console.error('[XOAuth1Modal] Hard reset error:', error);
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  const isConnected = status?.connected === true;
  const hasCredentials = status?.has_credentials === true;
  const screenName = status?.screen_name;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Connexion X</h2>
              <p className="text-xs text-zinc-400">OAuth 1.0a</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Connection Status */}
              {isConnected ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-emerald-400">Connecté</p>
                      {screenName && (
                        <p className="text-xs text-emerald-300/70">@{screenName}</p>
                      )}
                    </div>
                    <button
                      onClick={handleDisconnect}
                      className="text-xs text-zinc-400 hover:text-red-400 transition-colors"
                    >
                      Déconnecter
                    </button>
                  </div>
                </div>
              ) : hasCredentials ? (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-400">Non connecté</p>
                      <p className="text-xs text-amber-300/70">Credentials configurés - cliquez sur Connecter</p>
                    </div>
                  </div>
                </div>
              ) : null}
              
              {/* Callback URL Section */}
              <div className="bg-zinc-800/50 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Callback URL
                  </span>
                  <button
                    onClick={handleCopyCallback}
                    className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copié' : 'Copier'}
                  </button>
                </div>
                <code className="block text-xs text-zinc-400 bg-zinc-900 rounded-lg p-2 break-all">
                  {callbackUrl}
                </code>
                <p className="text-xs text-zinc-500">
                  Ajoutez cette URL dans votre app X Developer Portal
                </p>
              </div>
              
              {/* Credentials Form (only show if not connected) */}
              {!isConnected && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-zinc-300">
                    <Key className="w-4 h-4" />
                    API Credentials
                  </div>
                  
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="API Key (Consumer Key)"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="password"
                      placeholder="API Secret (Consumer Secret)"
                      value={apiSecret}
                      onChange={(e) => setApiSecret(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  
                  {(apiKey || apiSecret) && (
                    <button
                      onClick={handleSaveCredentials}
                      disabled={saving}
                      className="w-full bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg py-2 text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : (
                        'Sauvegarder les credentials'
                      )}
                    </button>
                  )}
                  
                  <a
                    href="https://developer.twitter.com/en/portal/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                  >
                    Obtenir les credentials sur X Developer Portal
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              
              {/* Connect Button */}
              {!isConnected && hasCredentials && (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="w-full bg-black hover:bg-zinc-800 text-white rounded-xl py-3 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      Connecter X
                    </>
                  )}
                </button>
              )}
              
              {/* Refresh Status */}
              {isConnected && (
                <button
                  onClick={fetchStatus}
                  disabled={loading}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
                  Actualiser le statut
                </button>
              )}
              
              {/* Error Display */}
              {status?.error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                  <p className="text-sm text-red-400">{status.error.message}</p>
                  {status.error.code === 'REAUTH_REQUIRED' && (
                    <button
                      onClick={handleConnect}
                      className="mt-2 text-xs text-red-300 underline"
                    >
                      Reconnecter
                    </button>
                  )}
                </div>
              )}
              
              {/* Hard Reset Button - Always visible */}
              <div className="pt-4 border-t border-zinc-800">
                <button
                  onClick={handleHardReset}
                  disabled={loading}
                  className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Réinitialiser (supprimer tout)
                </button>
                <p className="text-xs text-zinc-500 text-center mt-2">
                  Supprime API Key, tokens et toute la configuration X
                </p>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-800/30">
          <p className="text-xs text-zinc-500 text-center">
            OAuth 1.0a permet de publier des tweets avec images
          </p>
        </div>
      </div>
    </div>
  );
}

export default XOAuth1Modal;
